import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./index.css";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { firstPath } from "./lib/nav";
import AppShell from "./components/AppShell";
import NeedHelpButton from "./components/NeedHelpButton";
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";

import Dashboard from "./pages/Dashboard";
import Equipment from "./pages/Equipment";
import ActiveLab from "./pages/ActiveLab";
import Receiving from "./pages/Receiving";
import Worklist from "./pages/Worklist";
import Parts from "./pages/Parts";
import Subcontractor from "./pages/Subcontractor";
import SubcontractorRegister from "./pages/SubcontractorRegister";
import Scrapped from "./pages/Scrapped";
import Dispatch from "./pages/Dispatch";
import Due from "./pages/Due";
import Certificates from "./pages/Certificates";
import Customers from "./pages/Customers";
import RfqInbox from "./pages/RfqInbox";
import Reports from "./pages/Reports";
import Audit from "./pages/Audit";
import StockRegister from "./pages/StockRegister";
import DataImport from "./pages/DataImport";
import StaffUsers from "./pages/StaffUsers";
import CustomerUsers from "./pages/CustomerUsers";
import UsersPermissions from "./pages/UsersPermissions";
import JobFlowBoard from "./pages/JobFlowBoard";
import TechnicalDashboard from "./pages/TechnicalDashboard";
import Settings from "./pages/Settings";
import MyEquipment from "./pages/MyEquipment";
import RequestQuote from "./pages/RequestQuote";
import MyCertificates from "./pages/MyCertificates";
import CustHistory from "./pages/CustHistory";
import Documents from "./pages/Documents";
import Forms from "./pages/Forms";
import SopLibrary from "./pages/SopLibrary";
import SopDetail from "./pages/SopDetail";
import PortalDocuments from "./pages/PortalDocuments";
import PortalForms from "./pages/PortalForms";
import Reconciliation from "./pages/Reconciliation";
import Standards from "./pages/Standards";

function Splash() {
  return <div className="empty" style={{ paddingTop: 140 }}>Loading…</div>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset) return <Navigate to="/set-password" replace />;
  return children;
}

function Landing() {
  const { user, perms } = useAuth();
  if (!perms) return <Splash />;
  return <Navigate to={firstPath(perms, user?.user_type === "customer")} replace />;
}

function Gate({ module, children }) {
  const { user, perms, can } = useAuth();
  if (!perms) return <Splash />;
  if (!can(module, "view")) return <Navigate to={firstPath(perms, user?.user_type === "customer")} replace />;
  return children;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user && !user.must_reset) return <Navigate to="/app" replace />;
  if (user && user.must_reset) return <Navigate to="/set-password" replace />;
  return <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Landing />} />
            <Route path="dashboard" element={<Gate module="dashboard"><Dashboard /></Gate>} />
            <Route path="equipment" element={<Gate module="equipment"><Equipment /></Gate>} />
            <Route path="active-lab" element={<Gate module="active_lab"><ActiveLab /></Gate>} />
            <Route path="receiving" element={<Gate module="receiving"><Receiving /></Gate>} />
            <Route path="worklist" element={<Gate module="worklist"><Worklist /></Gate>} />
            <Route path="parts" element={<Gate module="parts"><Parts /></Gate>} />
            <Route path="subcontractor" element={<Gate module="subcontractor"><Subcontractor /></Gate>} />
            <Route path="subcontractor-register" element={<Gate module="subcontractor"><SubcontractorRegister /></Gate>} />
            <Route path="scrapped" element={<Gate module="scrapped"><Scrapped /></Gate>} />
            <Route path="dispatch" element={<Gate module="dispatch"><Dispatch /></Gate>} />
            <Route path="due" element={<Gate module="due"><Due /></Gate>} />
            <Route path="certificates" element={<Gate module="certificates"><Certificates /></Gate>} />
            <Route path="standards" element={<Gate module="standards"><Standards /></Gate>} />
            <Route path="customers" element={<Gate module="customers"><Customers /></Gate>} />
            <Route path="rfqs" element={<Gate module="rfqs"><RfqInbox /></Gate>} />
            <Route path="reports" element={<Gate module="reports"><Reports /></Gate>} />
            <Route path="reconciliation" element={<Gate module="reconciliation"><Reconciliation /></Gate>} />
            <Route path="audit" element={<Gate module="audit"><Audit /></Gate>} />
            <Route path="stock-register" element={<Gate module="stock_register"><StockRegister /></Gate>} />
            <Route path="import" element={<Gate module="import"><DataImport /></Gate>} />
            <Route path="users" element={<Gate module="users"><StaffUsers /></Gate>} />
            <Route path="customer-users" element={<Gate module="users"><CustomerUsers /></Gate>} />
            <Route path="users-permissions" element={<Gate module="users"><UsersPermissions /></Gate>} />
            <Route path="job-flow" element={<Gate module="job_flow"><JobFlowBoard /></Gate>} />
            <Route path="technical-dashboard" element={<Gate module="technical_dashboard"><TechnicalDashboard /></Gate>} />
            <Route path="settings" element={<Gate module="settings"><Settings /></Gate>} />
            <Route path="portal" element={<Gate module="portal"><MyEquipment /></Gate>} />
            <Route path="request-quote" element={<Gate module="request_quote"><RequestQuote /></Gate>} />
            <Route path="my-certificates" element={<Gate module="my_certificates"><MyCertificates /></Gate>} />
            <Route path="history" element={<Gate module="history"><CustHistory /></Gate>} />
            <Route path="documents" element={<Gate module="documents"><Documents /></Gate>} />
            <Route path="forms" element={<Gate module="forms"><Forms /></Gate>} />
            <Route path="sops" element={<Gate module="sops"><SopLibrary /></Gate>} />
            <Route path="sops/:id" element={<Gate module="sops"><SopDetail /></Gate>} />
            <Route path="portal-documents" element={<Gate module="documents"><PortalDocuments /></Gate>} />
            <Route path="portal-forms" element={<Gate module="forms"><PortalForms /></Gate>} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
        <NeedHelpButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
